import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/** Absolute-positioned "← Back" affordance for onboarding surfaces.
 *
 *  Behaviour:
 *    · If the caller is inside a multi-step wizard (`step > 0`), invoke
 *      `onStepBack()` — this decrements the wizard step in place without
 *      touching the router or dropping auth state.
 *    · Otherwise, `navigate(fallbackTo)` — falls back to the parent
 *      onboarding matrix (`/onboard`) by default. Uses `navigate(-1)` when
 *      the history stack has a viable prior entry inside our SPA to keep
 *      "cleanly reverse router state" honest; if not, we jump to the
 *      explicit `fallbackTo` route.
 *
 *  Never triggers a full-page reload → session cookie & JWT survive.
 */
export default function OnboardBackButton({
  step = 0,
  onStepBack,
  fallbackTo = "/onboard",
  label = "Back",
  testId = "onboard-back-btn",
}) {
  const navigate = useNavigate();

  const handle = () => {
    if (step > 0 && typeof onStepBack === "function") {
      onStepBack();
      return;
    }
    // Prefer SPA history pop when we came from within the app; else jump
    // to the parent onboarding matrix.
    if (window.history.length > 1 && document.referrer &&
        document.referrer.startsWith(window.location.origin)) {
      navigate(-1);
    } else {
      navigate(fallbackTo);
    }
  };

  return (
    <button
      type="button"
      onClick={handle}
      data-testid={testId}
      className="absolute top-5 left-5 z-30 inline-flex items-center gap-1.5 px-3 py-2 rounded-full
                 border border-white/15 bg-black/40 backdrop-blur
                 text-[10px] tracking-[0.25em] uppercase text-white/70
                 hover:text-white hover:border-white/35 transition-all"
      aria-label="Go back"
    >
      <ArrowLeft size={13} />
      <span>{label}</span>
    </button>
  );
}
